/**
 * Client-side shapes for Grammatik.
 *
 * These mirror `scripts/seed-grammatik-schema.ts`, which is the authoring
 * schema and the source of truth. They are restated rather than imported
 * because `scripts/` is build-time tooling that reads the filesystem and pulls
 * in Zod — none of which belongs in the browser bundle. The seed verifier is
 * what keeps the two honest: nothing reaches the database without matching the
 * schema first.
 */

export type Anchor = 'pt' | 'en' | 'both' | 'neither';

export type Bilingual = { en: string; pt: string };

/** A [start, end) range into the example's `de`, resolved at seed time. */
export type FocusSpan = [number, number];

export type LessonExample = {
  de: string;
  focus: FocusSpan[];
  en: string;
  pt: string;
};

export type GrammatikLesson = {
  n: number;
  slug: string;
  block: number;
  title: { de: string; en: string; pt: string };
  rule: Bilingual;
  anchor: Anchor;
  parallel: { de: string; en: string | null; pt: string | null } | null;
  trap: Bilingual | null;
  table: { headers: string[]; rows: string[][] } | null;
  examples: LessonExample[];
  introduces: string[];
  requires: string[];
  exercise_count: number;
};

type ExerciseBase = {
  id: string;
  concepts_used: string[];
  case_sensitive: boolean;
  lesson_n: number;
  lesson_slug: string;
};

export type FillBlankExercise = ExerciseBase & {
  kind: 'fill_blank';
  prompt_de: string;
  answer: string;
  accept: string[];
  hint: Bilingual | null;
};

export type ChoiceExercise = ExerciseBase & {
  kind: 'choice';
  prompt_de: string | null;
  instruction: Bilingual | null;
  options: string[];
  answer: string;
};

export type TransformExercise = ExerciseBase & {
  kind: 'transform';
  given_de: string;
  instruction: Bilingual;
  answer: string;
  accept: string[];
};

export type OrderExercise = ExerciseBase & {
  kind: 'order';
  chunks: string[];
  answer: string[];
  also_accept: string[][];
};

export type GrammatikPayload =
  | FillBlankExercise
  | ChoiceExercise
  | TransformExercise
  | OrderExercise;

/** The blank marker in a `fill_blank` prompt. Matches the authoring schema. */
export const BLANK = '___';

/** Every accepted free-text answer for one exercise. */
export function acceptedAnswers(payload: GrammatikPayload): string[] {
  switch (payload.kind) {
    case 'fill_blank':
      return [payload.answer, ...payload.accept];
    case 'transform':
      return [payload.answer, ...payload.accept];
    case 'choice':
      return [payload.answer];
    case 'order':
      return [payload.answer.join(' ')];
  }
}

/** The canonical correct answer, for the card back and the miss list. */
export function correctAnswerText(payload: GrammatikPayload): string {
  return payload.kind === 'order' ? payload.answer.join(' ') : payload.answer;
}

export type GrammatikState = {
  done_today: boolean;
  lessons_done: number[];
  highest_done: number;
  last_authored: number;
  next_lesson: GrammatikLesson | null;
  outline: {
    n: number;
    slug: string;
    block: number;
    authored: boolean;
    title: { de: string; en: string; pt: string } | null;
    /** YYYY-MM-DD the lesson was first completed, or null. */
    done_date: string | null;
  }[];
};

export type GrammatikSession = {
  session_id: number;
  mode: 'practice';
  lesson: GrammatikLesson | null;
  is_review_lesson: boolean;
  review_count: number;
  cards: unknown[];
};

/**
 * Display names for the ten blocks.
 *
 * The block NUMBER is curriculum data and lives in the database with each
 * lesson; only the name is presentation, so only the name is here. Ten strings
 * that change when the curriculum is restructured, which has happened once in
 * fifty lessons.
 */
export const BLOCK_NAMES: Record<number, string> = {
  1: 'The sentence and the verb',
  2: 'Nouns and gender',
  3: 'Akkusativ',
  4: 'Stem changes and modals',
  5: 'Separable verbs',
  6: 'Negation and possession',
  7: 'Dativ',
  8: 'Prepositions',
  9: 'The past',
  10: 'Closing essentials',
};
